import { defineStore } from 'pinia'
import { toRaw } from 'vue'
import { useApi } from '~/composables/useApi'
import type {
  CandySize,
  CasinoState,
  DailyQuestState,
  InvocationFirstShinyPityStatus,
  MinigamesSummary,
  RunContractState,
} from '~/stores/usePlayerStore'
import { syncInclusiveFromPlayerAccount } from '~/composables/useLocale'
import type { OfflineReport } from '~/types/game'
import { isAdminPlayModeEnabled } from '~/utils/adminShell'
import {
  decideServerUpdateResync,
  shouldReloadAfterServerUpdate,
} from '~/utils/serverUpdateResync'
import { usePlayerStore } from '~/stores/usePlayerStore'
import {
  useInventoryStore,
  isPokemonsDirty,
  isSavedTeamsDirty,
  resetPokemonsDirty,
  resetSavedTeamsDirty,
  snapshotPokemonsDirtyGen,
  getDirtyPokemonIds,
  clearDirtyPokemonIds,
} from '~/stores/useInventoryStore'
import { setLastUserIdForLocalBackup } from '~/utils/daycareReserveBackup'
import {
  hydrateDaycareFromServerPayload,
  persistDaycareLocalBackup,
  resetDaycareLevelGateBlock,
  syncDaycareToServer,
  flushDaycareBeforeOfflineDepart,
  tryRestoreDaycareFromPagehideBackup,
} from '~/composables/useDaycareSync'
import { useCombatStore } from '~/stores/useCombatStore'
import { useInvocationLockStore } from '~/stores/useInvocationLockStore'
import { useExpeditionStore } from '~/stores/useExpeditionStore'
import { useCommunityExpeditionStore, type CommunityExpeditionState } from '~/stores/useCommunityExpeditionStore'
import { useGmaxRaidStore } from '~/stores/useGmaxRaidStore'
import { getRarity } from '~/data/gacha'
import { getGenForSlug, getIdForSlug } from '~/data/pokedex'
import { useGameStore } from '~/stores/useGameStore'
import { reportClientError } from '~/composables/useErrorReporter'
import { toPublicErrorMessage } from '~/utils/publicErrorMessage'
import { trackSaveEnd, trackSaveStart } from '~/composables/useGameSyncStatus'
import { notifySaveFailure, notifySaveSuccess } from '~/composables/useSaveRecovery'
import {
  BATTLE_TOWER_CAPSTONE_ZONE_INDEX,
  getNextBattleTowerZoneIndex,
} from '~/data/battleTower'
import { repairCombatLoop, resetCombatLoopModuleState } from '~/composables/useCombatLoop'
import { isGameTabLeader } from '~/composables/useMultiTabGuard'
import { collectRuleUnlockedCosmeticIds, mergeLifetimeUnlockedCosmeticIds } from '~/data/cosmetics'
import { reconcilePokedexCompletion } from '~/utils/pokedexCompletion'
import {
  markOfflineDepartBeaconSent,
  resetOfflineDepartBeaconState,
  suppressOfflineDepartBeacon,
} from '~/composables/useOfflinePresence'
import { consumeAscensionHardReloadPending } from '~/utils/ascensionReload'

interface AuthState {
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  gameLoaded: boolean
  gameLoading: boolean
  gameLoadError: string | null
  user: {
    id: number
    username: string
    email: string
    role: 'user' | 'admin' | 'helper'
    betaAccess: boolean
    supporterTier?: number
    /** Whitelist admin : accès pendant la maintenance globale */
    maintenanceBypass: boolean
    /**
     * Pour `role === 'admin'` uniquement : périmètre restreint lecture + modération (+ notes support).
     */
    adminCapabilityScope?: 'support' | null
  } | null
  sessionToken: string | null
  maintenanceActive: boolean
  cooldownActive: boolean
  /** Flags globaux (GET /api/game/load → features) */
  battleTowerLategameDisabled: boolean
  serverBootId: string | null
}

interface LoginResponse {
  id: number
  username: string
  email: string
  role: 'user' | 'admin' | 'helper'
  betaAccess?: boolean
  supporterTier?: number
  maintenanceBypass?: boolean
  adminCapabilityScope?: 'support' | null
  sessionToken?: string
}

interface LoadGameResponse {
  serverBootId?: string
  features?: {
    battleTowerLategameDisabled?: boolean
    goldWellEnabled?: boolean
    ascensionSynergyEnabled?: boolean
    prestigeVaultEnabled?: boolean
    ascensionShinyBonusEnabled?: boolean
    zoneMasteryRerunAccelEnabled?: boolean
    ascensionAwakeningPackEnabled?: boolean
    ascensionRunMutatorsEnabled?: boolean
  }
  player: {
    id: number
    username: string
    gold: number
    xp: number
    level: number
    currentGeneration: number
    currentZone: number
    currentStage: number
    teamDpsBonus?: number
    badges: number
    offlineCapHours?: number
    offlineGoldEfficiencyPercent?: number
    dailyQuests?: DailyQuestState[]
    invocationPity?: Record<
      string,
      { withoutEpicPlus: number; withoutLegendary: number; withoutNew: number }
    >
    invocationFirstShinyPity?: InvocationFirstShinyPityStatus
    casino?: CasinoState
    minigames?: MinigamesSummary | null
    selectedTitle?: string | null
    selectedAvatarFrame?: string | null
    selectedProfileBanner?: string | null
    selectedHudTheme?: string | null
    ascension?: {
      level?: number
      points?: number
      spent?: number
      upgrades?: {
        xpBoost?: number
        goldBoost?: number
        enemyMitigation?: number
        starterMomentum?: number
      }
    }
  }
  offlineReport?: OfflineReport | null
  offlineReportHistory?: OfflineReport[]
  offlineReportStatus?: 'loading' | 'claimable' | 'cooldown'
  pokemons: Array<{
    id: number
    speciesId: number
    slug: string
    nameFr: string
    nameEn: string
    level: number
    xp: number
    stars: number
    isShiny: boolean
    teamSlot: number | null
  }>
}

interface OfflineReportSyncResponse {
  changed: boolean
  report?: OfflineReport | null
  history?: OfflineReport[]
  status?: 'claimable' | 'cooldown'
  dailyQuests?: DailyQuestState[]
  reload?: boolean
  pokemonsChanged?: boolean
}

export type SaveGameStateOptions = {
  /** Persiste les Pokémon avant le profil (éclosion pension : ★ en base avant les slots). */
  pokemonsFirst?: boolean
  /** Envoie save-pokemons même si le dirty flag est vide (après éclosion). */
  forcePokemons?: boolean
}

export type LoadGameStateOptions = {
  /** Après ascension : ne pas ré-uploader l’ancienne collection avant le GET /load. */
  skipPreSave?: boolean
  /**
   * Conserve le buffer or farm local après reload (évite saut HUD quand le load
   * sert à resync après une erreur dépense / admin override mid-session).
   */
  preserveCombatGoldAccrual?: boolean
}

// Save lock to prevent concurrent server saves (race condition causes pokemon duplication)
let _saveLock = false
let _savePending = false
// Guard: prevents saves until game state is loaded from server (avoids sending empty/default data)
let _gameLoaded = false
/** Évite le flush « Pokémon dirty » avant load quand loadGameState est rappelé depuis save (reload admin). */
let _loadGameStateDepth = 0
// Throttle: minimum interval between actual saves (ms)
/** Intervalle minimum entre deux POST /game/save (réduit la contention FOR UPDATE côté serveur). */
const SAVE_THROTTLE_MS = 30_000
let _lastSaveTime = 0
let _throttleTimer: ReturnType<typeof setTimeout> | null = null
let _serverUpdateReloadTriggered = false
/** Un rejeu après `SERVER_UPDATED` a réussi : recharger dès que la sauvegarde est complète. */
let _serverUpdateReloadPending = false
let _offlineReportHydrationAttempted = false
let _offlineReportHydrationInFlight = false
let _offlineReportRetryTimer: ReturnType<typeof setTimeout> | null = null
/** Rapport offline déjà acquitté localement — bloque un reload tardif de hydrateOfflineReportInBackground. */
let _offlineReportAcknowledged = false
let _invocationDeferredSaveTimer: ReturnType<typeof setTimeout> | null = null
/** Coalesce gacha follow-up saves (stats/quêtes) — évite 2× save + save-pokemons par clic. */
let _postGachaSaveTimer: ReturnType<typeof setTimeout> | null = null
const POST_GACHA_SAVE_DEBOUNCE_MS = 2_500
let _saveRateLimitRetryTimer: ReturnType<typeof setTimeout> | null = null
/** Ignore les 409 « autre session » juste après ascension (requêtes en vol avec l’ancien jeton). */
let _ascensionResetGraceUntil = 0
/** Bloque checkpoint / save automatiques pendant confirmAscend → loadGameState. */
let _ascensionFlowActive = false
/** Dernier état Descente confirmé par le serveur (évite d’omettre une fin de run `null`). */
let _lastPersistedDescenteRunJson: string | null = null
/** Sérialise les GET /load concurrents (ex. navigateTo + confirmAscend en parallèle). */
let _loadGameStateInFlight: Promise<null> | null = null

function sleepMs(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

export function isAscensionFlowActive(): boolean {
  return _ascensionFlowActive
}

function descenteRunSnapshotJson(run: unknown): string {
  try {
    return JSON.stringify(run ?? null)
  } catch {
    return 'null'
  }
}

function scheduleSaveAfterRateLimit(retryAfterSec?: number) {
  if (_saveRateLimitRetryTimer) return
  const delayMs = Math.max(1_500, Math.ceil((retryAfterSec ?? 4) * 1000))
  _saveRateLimitRetryTimer = setTimeout(() => {
    _saveRateLimitRetryTimer = null
    void useAuthStore()
      .saveGameState(false, true)
      .catch((e: unknown) => {
        console.warn('[SAVE] Rate-limit retry failed:', e)
      })
  }, delayMs)
}

/** Dedupe blocking alerts when several API calls return 409 in parallel. */
let _sessionConflictAlertShown = false
let _sessionResyncInFlight: Promise<void> | null = null

function toSavePlainValue(value: unknown, seen = new WeakSet<object>(), depth = 0): unknown {
  if (value == null || typeof value !== 'object') return value
  const raw = toRaw(value) as object
  if (seen.has(raw)) return null
  if (depth > 24) return null
  seen.add(raw)
  if (Array.isArray(raw)) {
    return raw.map((entry) => toSavePlainValue(entry, seen, depth + 1))
  }
  if (raw instanceof Date) return raw.toISOString()
  const out: Record<string, unknown> = {}
  for (const [key, entry] of Object.entries(raw)) {
    if (typeof entry === 'function') continue
    out[key] = toSavePlainValue(entry, seen, depth + 1)
  }
  return out
}

/** Horodatage du dernier reload « serveur mis à jour », conservé à travers le reload. */
const SERVER_UPDATE_RELOAD_KEY = 'pokeidle:serverUpdatedReloadAt'

function readLastServerUpdateReloadAt(): number | null {
  try {
    if (typeof sessionStorage === 'undefined') return null
    const raw = Number(sessionStorage.getItem(SERVER_UPDATE_RELOAD_KEY))
    return Number.isFinite(raw) && raw > 0 ? raw : null
  } catch {
    return null
  }
}

/**
 * Adopte le `serverBootId` renvoyé par le serveur sur un `SERVER_UPDATED`.
 * Retourne true si l'écriture refusée doit être rejouée avec ce nouvel id.
 *
 * Le serveur refuse l'écriture AVANT toute persistance : rejouer est le seul
 * moyen de ne pas perdre ce que la requête transportait.
 */
function resyncServerBootId(
  store: { serverBootId: string | null },
  res: GameSavePlayerResult | null | undefined,
  retried: boolean,
): boolean {
  const decision = decideServerUpdateResync(res, retried)
  if (decision.action !== 'retry') return false
  store.serverBootId = decision.bootId
  return true
}

/**
 * Recharge la page pour récupérer le nouveau bundle — mais seulement si l'écriture
 * a fini par être persistée, et au plus une fois par fenêtre de refroidissement.
 * Recharger avec une écriture encore en attente détruirait la progression.
 */
function triggerServerUpdatedReload(writePersisted: boolean): boolean {
  if (typeof window === 'undefined') return false
  if (_serverUpdateReloadTriggered) return true
  const nowMs = Date.now()
  if (
    !shouldReloadAfterServerUpdate({
      writePersisted,
      lastReloadAtMs: readLastServerUpdateReloadAt(),
      nowMs,
    })
  ) {
    return false
  }
  _serverUpdateReloadTriggered = true
  try {
    sessionStorage.setItem(SERVER_UPDATE_RELOAD_KEY, String(nowMs))
  } catch {
    /* sessionStorage indisponible : le plafond saute, pas le reload */
  }
  window.location.reload()
  return true
}

/** Réponse partielle de POST /api/game/save (or / nonce). */
export interface GameSavePlayerResult {
  reload?: boolean
  code?: string
  serverBootId?: string
  gold?: number
  goldNonce?: number
  totalKills?: number
  totalKillsAdjusted?: boolean
  dailyQuests?: DailyQuestState[]
  runContracts?: RunContractState[]
  /** False si le serveur n’a pas appliqué l’or du payload (nonce client obsolète). */
  goldApplied?: boolean
}

/** Wait until no save is in flight (true = unlocked before timeout). */
function waitForSaveLock(maxMs = 4000): Promise<boolean> {
  return new Promise((resolve) => {
    const start = Date.now()
    const tick = () => {
      if (!_saveLock) {
        resolve(true)
        return
      }
      if (Date.now() - start > maxMs) {
        resolve(false)
        return
      }
      setTimeout(tick, 50)
    }
    tick()
  })
}

// Session token storage key (sessionStorage reduces XSS persistence risk vs localStorage)
const SESSION_TOKEN_KEY = 'poke-idle-session-token'

function loadSessionTokenFromStorage(): string | null {
  if (typeof sessionStorage === 'undefined') return null
  const raw = sessionStorage.getItem(SESSION_TOKEN_KEY)
  return raw && raw.length > 0 ? raw : null
}

function persistSessionTokenToStorage(token: string | null) {
  if (typeof sessionStorage === 'undefined') return
  if (token && token.length > 0) {
    sessionStorage.setItem(SESSION_TOKEN_KEY, token)
    return
  }
  sessionStorage.removeItem(SESSION_TOKEN_KEY)
}

function cancelPendingGameSaves() {
  _savePending = false
  if (_throttleTimer) {
    clearTimeout(_throttleTimer)
    _throttleTimer = null
  }
  if (_invocationDeferredSaveTimer) {
    clearTimeout(_invocationDeferredSaveTimer)
    _invocationDeferredSaveTimer = null
  }
  if (_postGachaSaveTimer) {
    clearTimeout(_postGachaSaveTimer)
    _postGachaSaveTimer = null
  }
  if (_saveRateLimitRetryTimer) {
    clearTimeout(_saveRateLimitRetryTimer)
    _saveRateLimitRetryTimer = null
  }
}

function handleSessionConflict409(store: { logout: (opts?: { skipSave?: boolean }) => void | Promise<void> }) {
  if (Date.now() < _ascensionResetGraceUntil) return
  if (_ascensionFlowActive) return
  if (_sessionConflictAlertShown) {
    void store.logout({ skipSave: true })
    return
  }
  _sessionConflictAlertShown = true
  try {
    alert('Une session a été ouverte sur un autre appareil. Veuillez rafraîchir la page.')
  } finally {
    void store.logout({ skipSave: true })
  }
}

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    isAuthenticated: false,
    isLoading: false,
    error: null,
    gameLoaded: false,
    gameLoading: false,
    gameLoadError: null,
    user: null,
    // Load sessionToken from sessionStorage (tab lifetime only).
    sessionToken: loadSessionTokenFromStorage(),
    maintenanceActive: false,
    cooldownActive: false,
    battleTowerLategameDisabled: false,
    serverBootId: null,
  }),

  actions: {
    setSessionToken(token: string | null) {
      this.sessionToken = token
      persistSessionTokenToStorage(token)
    },

    /**
     * Après ascension : nouveau jeton, annule les sauvegardes en attente et ignore
     * brièvement les 409 des requêtes encore parties avec l’ancien jeton.
     */
    beginAscensionResetSession(sessionToken: string) {
      cancelPendingGameSaves()
      _sessionConflictAlertShown = false
      _ascensionResetGraceUntil = Date.now() + 20_000
      this.setSessionToken(sessionToken)
    },

    /**
     * Jeton régénéré par le serveur pour CET appareil (ex. changement de mot de passe) :
     * même traitement que l'ascension (requêtes en vol avec l'ancien jeton ignorées).
     */
    adoptRotatedSessionToken(sessionToken: string) {
      this.beginAscensionResetSession(sessionToken)
    },

    setAscensionFlowActive(active: boolean) {
      _ascensionFlowActive = active
      if (active) {
        cancelPendingGameSaves()
        _ascensionResetGraceUntil = Date.now() + 30_000
      }
    },

    /**
     * Reçu via WebSocket (`session:revoked`) quand une connexion s'ouvre ailleurs :
     * déconnexion immédiate, sans sauvegarde (la nouvelle session est désormais
     * autoritaire). Réutilise l'UX du conflit 409.
     */
    handleRemoteSessionRevoked() {
      if (!this.isAuthenticated) return
      handleSessionConflict409(this)
    },

    /**
     * Le cookie de session peut survivre sans sessionStorage (veille longue, onglet restauré, etc.).
     * Sans jeton, les POST jeu renvoient 409 alors que GET /load fonctionne encore.
     */
    async resyncSessionTokenIfMissing() {
      if (this.sessionToken || !this.isAuthenticated) return
      if (!_sessionResyncInFlight) {
        _sessionResyncInFlight = (async () => {
          try {
            const api = useApi()
            const r = await api.get<LoginResponse>('/api/auth/me')
            if (r.sessionToken) {
              this.setSessionToken(r.sessionToken)
            }
          } catch (e) {
            console.warn('[AUTH] Session token resync failed:', e)
          } finally {
            _sessionResyncInFlight = null
          }
        })()
      }
      await _sessionResyncInFlight
    },

    /**
     * Si maintenance active et compte non autorisé : bloque le chargement du jeu (même logique que checkAuth).
     * Retourne true si on doit s'arrêter (sans lever d'erreur).
     */
    async guardMaintenanceAfterAuth(): Promise<boolean> {
      const u = this.user
      if (!u || u.role === 'admin' || u.maintenanceBypass) {
        this.maintenanceActive = false
        return false
      }
      try {
        const api = useApi()
        const maintenance = await api.get<{ enabled: boolean; message?: string }>('/api/maintenance')
        if (maintenance.enabled) {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem('maintenance_message', maintenance.message || 'Maintenance en cours')
          }
          this.maintenanceActive = true
          this.cooldownActive = false
          return true
        }
      } catch {
        /* continue */
      }
      this.maintenanceActive = false
      return false
    },

    async guardCooldownAfterAuth(): Promise<boolean> {
      if (this.maintenanceActive) {
        this.cooldownActive = false
        return false
      }
      const u = this.user
      if (!u || u.role === 'admin' || u.maintenanceBypass) {
        this.cooldownActive = false
        return false
      }
      try {
        const api = useApi()
        const cooldown = await api.get<{ enabled: boolean; blockAccess?: boolean; message?: string }>(
          '/api/cooldown',
        )
        if (cooldown.enabled && cooldown.blockAccess !== false) {
          if (typeof sessionStorage !== 'undefined') {
            sessionStorage.setItem(
              'cooldown_message',
              cooldown.message ||
                'Le serveur fait une courte pause. Revenez dans quelques instants.',
            )
          }
          this.cooldownActive = true
          return true
        }
      } catch {
        /* continue */
      }
      this.cooldownActive = false
      return false
    },

    async register(username: string, email: string, password: string) {
      this.isLoading = true
      this.error = null
      try {
        const api = useApi()
        const response = await api.post<LoginResponse>('/api/auth/register', { username, email, password })
        this.user = {
          ...response,
          betaAccess: response.betaAccess ?? false,
          maintenanceBypass: response.maintenanceBypass ?? false,
          adminCapabilityScope:
            response.role === 'admin' && response.adminCapabilityScope === 'support'
              ? 'support'
              : null,
        }
        this.setSessionToken(response.sessionToken ?? null)
        if (await this.guardMaintenanceAfterAuth()) return
        if (await this.guardCooldownAfterAuth()) return
        this.isAuthenticated = true
        await this.loadGameState()
      } catch (e: any) {
        this.error = e.message
        throw e
      } finally {
        this.isLoading = false
      }
    },

    async login(email: string, password: string) {
      this.isLoading = true
      this.error = null
      try {
        const api = useApi()
        const response = await api.post<LoginResponse>('/api/auth/login', { email, password })
        this.user = {
          ...response,
          betaAccess: response.betaAccess ?? false,
          maintenanceBypass: response.maintenanceBypass ?? false,
          adminCapabilityScope:
            response.role === 'admin' && response.adminCapabilityScope === 'support'
              ? 'support'
              : null,
        }
        this.setSessionToken(response.sessionToken ?? null)
        if (await this.guardMaintenanceAfterAuth()) return
        if (await this.guardCooldownAfterAuth()) return
        this.isAuthenticated = true
        await this.loadGameState()
      } catch (e: any) {
        this.error = e.message
        throw e
      } finally {
        this.isLoading = false
      }
    },

    async logout(options?: {
      skipSave?: boolean
    }) {
      if (options?.skipSave) {
        suppressOfflineDepartBeacon()
      }
      try {
        const api = useApi()
        if (!options?.skipSave && this.isAuthenticated && _gameLoaded) {
          const player = usePlayerStore()
          const game = useGameStore()
          game.saveOfflineSnapshot()
          await this.resyncSessionTokenIfMissing()
          await flushDaycareBeforeOfflineDepart()
          if (this.sessionToken) {
            try {
              await api.post('/api/game/offline-depart', {
                snapshotDps: game.snapshotDps,
                snapshotZoneDps: game.snapshotZoneDps,
                snapshotGoldRate: game.snapshotGoldRate,
                snapshotEnemyMaxHp: game.snapshotEnemyMaxHp,
                snapshotGoldReward: game.snapshotGoldReward,
                ...(player.combatGeneration != null &&
                player.combatZone != null &&
                !player.isFrontierReturnGuarded()
                  ? {
                      combatGeneration: player.combatGeneration,
                      combatZone: player.combatZone,
                    }
                  : {}),
                adminVersion: player.adminVersion,
                sessionToken: this.sessionToken,
                serverBootId: this.serverBootId,
              })
              markOfflineDepartBeaconSent()
            } catch (e: unknown) {
              console.warn('[OFFLINE-DEPART] Failed:', e)
            }
          }
          await this.saveGameState(false, true)
        }
        await api.post('/api/auth/logout')
      } catch {
        // ignore logout errors
      }
      _sessionConflictAlertShown = false
      this.isAuthenticated = false
      this.user = null
      this.setSessionToken(null)
      this.battleTowerLategameDisabled = false
      this.serverBootId = null
      this.gameLoaded = false
      this.gameLoading = false
      this.gameLoadError = null
      _gameLoaded = false
      _offlineReportHydrationAttempted = false
      _offlineReportHydrationInFlight = false
      _offlineReportAcknowledged = false
      if (_offlineReportRetryTimer) {
        clearTimeout(_offlineReportRetryTimer)
        _offlineReportRetryTimer = null
      }
      useGameStore().closeOfflineReport()
      resetCombatLoopModuleState()
      useCombatStore().reset()
      const player = usePlayerStore()
      player.reset()
      navigateTo('/guide')
    },

    async checkAuth(options: { loadGameState?: boolean } = {}) {
      const shouldLoadGameState = options.loadGameState ?? true
      try {
        const api = useApi()
        const response = await api.get<LoginResponse>('/api/auth/me')
        const { sessionToken: tokenFromMe, ...meRest } = response
        this.user = {
          ...meRest,
          betaAccess: response.betaAccess ?? false,
          maintenanceBypass: response.maintenanceBypass ?? false,
          adminCapabilityScope:
            response.role === 'admin' && response.adminCapabilityScope === 'support'
              ? 'support'
              : null,
        }
        if (tokenFromMe) {
          this.setSessionToken(tokenFromMe)
        }

        if (await this.guardMaintenanceAfterAuth()) return
        if (await this.guardCooldownAfterAuth()) return

        this.isAuthenticated = true
        if (shouldLoadGameState) {
          await this.loadGameState()
        }
      } catch {
        this.isAuthenticated = false
        this.user = null
        this.gameLoaded = false
        this.gameLoading = false
        this.gameLoadError = null
        _gameLoaded = false
        _offlineReportHydrationAttempted = false
        _offlineReportHydrationInFlight = false
        _offlineReportAcknowledged = false
        if (_offlineReportRetryTimer) {
          clearTimeout(_offlineReportRetryTimer)
          _offlineReportRetryTimer = null
        }
      }
    },

    async ensureGameLoaded() {
      if (!this.isAuthenticated || _gameLoaded) return
      if (this.user?.role === 'admin' && !isAdminPlayModeEnabled()) {
        _gameLoaded = true
        this.gameLoaded = true
        return
      }
      await this.loadGameState()
    },

    async loadGameState(options?: LoadGameStateOptions) {
      if (!this.isAuthenticated) return null
      if (this.user?.role === 'admin' && !isAdminPlayModeEnabled()) {
        _gameLoaded = true
        this.gameLoaded = true
        return null
      }
      if (_loadGameStateInFlight) {
        await _loadGameStateInFlight
        return null
      }
      _loadGameStateInFlight = this._loadGameStateBody(options).finally(() => {
        _loadGameStateInFlight = null
      })
      return _loadGameStateInFlight
    },

    async _loadGameStateBody(options?: LoadGameStateOptions) {
      if (!this.isAuthenticated) return null
      const afterAscensionHardReload = consumeAscensionHardReloadPending()
      _loadGameStateDepth++
      const isOutermostLoad = _loadGameStateDepth === 1
      try {
        const playerBeforeLoad = usePlayerStore()
        if (isOutermostLoad && _gameLoaded && isPokemonsDirty() && !options?.skipPreSave) {
          try {
            await this.saveGameState(false, true)
          } catch {
            /* best effort : évite d’effacer des évolutions / changements non persistés */
          }
        }
        if (_gameLoaded && !_ascensionFlowActive) {
          try {
            const { flushCombatGoldCredit } = await import('~/composables/useCombatGoldCredit')
            await flushCombatGoldCredit({ force: true, maxPasses: 3 })
          } catch {
            /* best effort avant reload */
          }
        }
      this.gameLoading = true
      this.gameLoadError = null
      _gameLoaded = false
      this.gameLoaded = false
      try {
        const api = useApi()
        useGameStore().setOfflineReportLoading()
        let data: LoadGameResponse | null = null
        let lastLoadError: unknown = null
        for (let attempt = 0; attempt < 6; attempt++) {
          try {
            data = await api.get<LoadGameResponse>('/api/game/load', { timeoutMs: 25000 })
            break
          } catch (e: unknown) {
            lastLoadError = e
            const status = (e as { status?: number })?.status
            if ((status === 503 || status === 429) && attempt < 5) {
              const retryAfter = Math.max(
                1,
                Math.ceil(Number((e as { retryAfter?: number })?.retryAfter ?? 2)),
              )
              await sleepMs(retryAfter * 1000)
              continue
            }
            throw e
          }
        }
        if (!data) {
          throw lastLoadError ?? new Error('Impossible de charger la progression.')
        }
        this.serverBootId = data.serverBootId ?? null
        this.battleTowerLategameDisabled = Boolean(data.features?.battleTowerLategameDisabled)
        const player = usePlayerStore()
        const inventory = useInventoryStore()
        const game = useGameStore()
        const serverCapstoneDefeated =
          Math.max(
            0,
            Math.floor(
              Number((data.player as { stats?: Record<string, number> }).stats?._btCapstoneDefeated ?? 0),
            ),
          ) >= 1
        const localCapstoneVictory =
          player.battleTowerCapstoneDefeated ||
          Math.max(
            0,
            Math.floor(Number((player.stats as Record<string, number> | undefined)?._btCapstoneDefeated ?? 0)),
          ) >= 1
        const isNgPlusFreshRun =
          ((data.player as { defeatedBosses?: string[] }).defeatedBosses ?? []).length === 0 &&
          ((data.player as { battleTowerDefeatedTypes?: unknown[] }).battleTowerDefeatedTypes ?? [])
            .length === 0 &&
          !serverCapstoneDefeated
        /** Sync offline Red → serveur ; ne pas réappliquer après reset ascension. */
        const capstoneVictoryBeforeLoad =
          !isNgPlusFreshRun && localCapstoneVictory && !serverCapstoneDefeated

        if (isNgPlusFreshRun) {
          player.clearEphemeralRunUiState()
          useCombatStore().reset()
          resetCombatLoopModuleState()
          useCommunityExpeditionStore().clearFromRunReset()
        }

        // Avant `setPlayer` : les prix / gains dérivés doivent être calculés avec
        // l'économie courante dès le premier rendu, sans attendre un push WS.
        player.applyEconomyModifiers((data.features as any)?.economy ?? null)

        const repairedBossKeys = player.setPlayer({
          username: data.player.username,
          gold: data.player.gold,
          xp: data.player.xp,
          level: data.player.level,
          currentGeneration: data.player.currentGeneration,
          currentZone: data.player.currentZone,
          currentStage: data.player.currentStage,
          stageKills: Math.max(
            0,
            Math.min(9, Math.floor(Number((data.player as any).stageKills ?? 0))),
          ),
          teamDpsBonus: data.player.teamDpsBonus ?? 0,
          badges: data.player.badges,
          defeatedBosses: (data.player as any).defeatedBosses ?? [],
          totalKills: (data.player as any).totalKills ?? 0,
          candies: (data.player as any).candies ?? { S: 0, M: 0, L: 0, XL: 0 },
          adminVersion: (data.player as any).adminVersion ?? 0,
          shinyCharms: (data.player as any).shinyCharms ?? 0,
          completedPokedexGens: (data.player as any).completedPokedexGens ?? [],
          goldWellLevel: (data.player as any).goldWellLevel ?? 0,
          goldWellEnabled: Boolean(data.features?.goldWellEnabled),
          ascensionSynergyEnabled: Boolean(data.features?.ascensionSynergyEnabled),
          prestigeVaultEnabled: Boolean(data.features?.prestigeVaultEnabled),
          ascensionShinyBonusEnabled: Boolean(data.features?.ascensionShinyBonusEnabled),
          zoneMasteryRerunAccelEnabled: Boolean(data.features?.zoneMasteryRerunAccelEnabled),
          ascensionAwakeningPackEnabled: Boolean(data.features?.ascensionAwakeningPackEnabled),
          ascensionRunMutatorsEnabled: Boolean(data.features?.ascensionRunMutatorsEnabled),
          ascensionTranscendenceLevel: (data.player as any).ascensionTranscendenceLevel ?? 0,
          ascensionKeystones: ((data.player as any).ascensionKeystones ?? []) as string[],
          penaltyType: (data.player as any).penaltyType ?? null,
          penaltyPercent: (data.player as any).penaltyPercent ?? 0,
          avatarUrl: (data.player as any).avatarUrl ?? null,
          stats: (data.player as any).stats ?? {},
          showcase: (data.player as any).showcase ?? [],
          achievements: (data.player as any).achievements ?? [],
          selectedTitle: (data.player as any).selectedTitle ?? null,
          selectedAvatarFrame: (data.player as any).selectedAvatarFrame ?? null,
          selectedProfileBanner: (data.player as any).selectedProfileBanner ?? null,
          selectedHudTheme: (data.player as any).selectedHudTheme ?? null,
          hideTeamFromProfile: (data.player as any).hideTeamFromProfile === true,
          supporterTier: Math.max(0, Math.min(3, Math.floor(Number((data.player as any).supporterTier) || 0))),
          supporterSince: (data.player as any).supporterSince ?? null,
          hideSupporterBadge: (data.player as any).hideSupporterBadge === true,
          newInterfaceEnabled: (data.player as any).newInterfaceEnabled === true,
          communityUnlockedCosmeticIds: Array.isArray((data.features as any)?.communityUnlockedCosmeticIds)
            ? (data.features as any).communityUnlockedCosmeticIds.filter(
                (id: unknown): id is string => typeof id === 'string' && id.length > 0,
              )
            : [],
          serverGoalPercentage: Math.max(
            0,
            Math.min(
              100,
              Number((data.features as any)?.serverGoalPercentage) || 0,
            ),
          ),
          ascensionLevel: (data.player as any).ascension?.level ?? 0,
          ascensionPoints: (data.player as any).ascension?.points ?? 0,
          ascensionSpent: (data.player as any).ascension?.spent ?? 0,
          ascensionUpgrades: {
            xpBoost: (data.player as any).ascension?.upgrades?.xpBoost ?? 0,
            goldBoost: (data.player as any).ascension?.upgrades?.goldBoost ?? 0,
            enemyMitigation: (data.player as any).ascension?.upgrades?.enemyMitigation ?? 0,
            starterMomentum: (data.player as any).ascension?.upgrades?.starterMomentum ?? 0,
          },
          audioPreferences: (data.player as any).audioPreferences ?? undefined,
          inclusiveWriting: (data.player as any).inclusiveWriting === true,
          chosenStarters: (data.player as any).chosenStarters ?? {},
          goldNonce: (data.player as any).goldNonce ?? 0,
          battleTowerDefeatedTypes: (data.player as any).battleTowerDefeatedTypes ?? [],
          battleTowerZone: (data.player as any).battleTowerZone ?? null,
          descenteSanctuaireShards: (data.player as any).descenteSanctuaireShards ?? 0,
          descenteSanctuaireRun: (data.player as any).descenteSanctuaireRun ?? null,
          tokens: (data.player as any).tokens ?? 0,
          extraDaycareSlots: (data.player as any).extraDaycareSlots ?? 0,
          offlineCapHours: (data.player as any).offlineCapHours ?? 6,
          offlineGoldEfficiencyPercent: (data.player as any).offlineGoldEfficiencyPercent ?? 40,
          offlineShopProgress: (data.player as any).offlineShopProgress ?? undefined,
          dailyQuests: (data.player as any).dailyQuests ?? [],
          runContracts: (data.player as any).runContracts ?? [],
          weeklyQuests: (data.player as any).weeklyQuests ?? [],
          monthlyQuests: (data.player as any).monthlyQuests ?? [],
          regularityState: (data.player as any).regularityState ?? undefined,
          monthlyCalendar: (data.player as any).monthlyCalendar ?? undefined,
          invocationPity: (data.player as any).invocationPity ?? {},
          invocationFirstShinyPity: (data.player as any).invocationFirstShinyPity ?? undefined,
          casino: (data.player as any).casino,
          minigames: (data.player as any).minigames ?? null,
          combatAutomation: (data.player as any).combatAutomation ?? undefined,
          lastSelectedFarmRoute: (data.player as any).lastSelectedFarmRoute ?? null,
          favoriteFarmRoute: (data.player as any).favoriteFarmRoute ?? null,
          offlineShinyReveal: (data.player as any).offlineShinyReveal ?? undefined,
          role: (data.player as any).role === 'admin' ? 'admin' : 'user',
          battleTowerLategameDisabled: this.battleTowerLategameDisabled,
          isLoggedIn: true,
        })

        // Tour / épreuves sans clé champion en base → badges + isEndgame incohérents (même logique que load serveur)
        const towerTypesRepair = (data.player as any).battleTowerDefeatedTypes ?? []
        const towerZnRepair = (data.player as any).battleTowerZone
        const hasChampionKey =
          player.defeatedBosses.includes('g9z13') || player.defeatedBosses.includes('geeta')
        let towerChampionKeyRepaired = false
        if (
          !hasChampionKey &&
          (towerTypesRepair.length > 0 || towerZnRepair != null)
        ) {
          player.defeatedBosses = [...new Set([...player.defeatedBosses, 'g9z13'])]
          player.badges = Math.max(player.badges ?? 0, player.defeatedBosses.length)
          towerChampionKeyRepaired = true
        }

        player.applyCombatFarmFromServer(
          (data.player as any).combatGeneration,
          (data.player as any).combatZone,
        )

        const offlineReportStatus = data.offlineReportStatus ?? 'cooldown'
        player.tryRestoreFarmRouteAfterOfflineReturn({
          offlineReportPending: offlineReportStatus === 'claimable',
        })

        syncInclusiveFromPlayerAccount(player.inclusiveWriting)

        const cosmeticRepairCtx = {
          level: player.level,
          badges: player.badges,
          achievements: player.achievements ?? [],
          stats: (player.stats ?? {}) as Record<string, number>,
          completedPokedexGens: player.completedPokedexGens ?? [],
          totalKills: player.totalKills,
        }
        player.stats = mergeLifetimeUnlockedCosmeticIds(
          player.stats,
          collectRuleUnlockedCosmeticIds(cosmeticRepairCtx),
        ) as Record<string, number>

        // Post-champion : l’accueil combat = tour (épreuves / réserve), pas les sauvages de Paldea z13
        const canTowerLoad =
          !player.battleTowerLategameDisabled || player.role === 'admin'
        if (
          !player.isFrontierReturnGuarded() &&
          player.isEndgame &&
          player.combatGeneration === null &&
          player.battleTowerZone == null &&
          canTowerLoad
        ) {
          player.enterBattleTowerAtProgress()
        }

        // Victoire Red locale pas encore sur le serveur : ne pas rouvrir la zone 19 au reload offline.
        if (capstoneVictoryBeforeLoad) {
          if (!player.battleTowerCapstoneDefeated) {
            player.markBattleTowerCapstoneDefeated()
          }
          if (player.battleTowerZone === BATTLE_TOWER_CAPSTONE_ZONE_INDEX) {
            player.enterBattleTower(
              getNextBattleTowerZoneIndex(player.battleTowerDefeatedTypes, true),
            )
          }
        }

        // Update auth user with betaAccess from server
        if (this.user) {
          this.user.betaAccess = (data.player as any).betaAccess ?? false
          this.user.supporterTier = Math.max(
            0,
            Math.min(3, Math.floor(Number((data.player as any).supporterTier) || 0)),
          )
          if ((data.player as any).role) this.user.role = (data.player as any).role
        }

        // Overwrite localStorage bonuses with server data
        player.saveBonuses()

        inventory.collection = data.pokemons.map((p, i) => ({
          id: i + 1,
          serverId: (p as any).id ?? null,
          slug: p.slug,
          nameFr: p.nameFr,
          nameEn: p.nameEn,
          level: p.level,
          xp: p.xp,
          stars: p.stars,
          isShiny: p.isShiny,
          rarity: (p as any).rarity ?? getRarity(p.slug),
          teamSlot: p.teamSlot,
          isFavorite: (p as any).isFavorite === true,
          cosmeticFormSlug: (p as any).cosmeticFormSlug ?? null,
        }))
        inventory.nextId = data.pokemons.length + 1
        // Convert legacy standalone Gigamax entries into forms on their base Pokémon.
        inventory.migrateGmaxForms()
        inventory.sanitizeUnownedGmaxForms()
        // Remove duplicates loaded from server (breaks re-save cycle)
        inventory.removeDuplicates()
        // Fix rarities for existing Pokemon that inherited wrong rarity from parent
        inventory.migrateRarities()
        // Fix stale FR/EN names
        inventory.migrateNames()
        inventory.reconcileLevelsToXpCurve()
        inventory.pruneFiltersIfCollectionEmpty()
        // Rebuild hasEvolved flags so checkAllEvolutions doesn't re-create existing evolutions
        inventory.rebuildHasEvolvedFlags()
        // Migrate legacy duplicate boss slugs (koga/bruno/lance → koga-gen2/bruno-gen2/lance-gen2)
        player.migrateBossSlugs()

        /** Inventaire = vérité serveur : évite saves « Pokémon dirty » sur une ancienne collection (après reset / bump admin_version). */
        resetPokemonsDirty()
        clearDirtyPokemonIds()
        _lastPersistedDescenteRunJson = descenteRunSnapshotJson(
          (data.player as { descenteSanctuaireRun?: unknown }).descenteSanctuaireRun ?? null,
        )

        // Mark game as loaded — saves are now safe
        const {
          combatGoldAccrual,
          resetCombatGoldAccrual,
          setCombatGoldAccrualBuffer,
        } = await import('~/composables/useCombatGoldCredit')
        const preservedFarmGold = options?.preserveCombatGoldAccrual
          ? Math.max(0, Math.floor(Number(combatGoldAccrual.value) || 0))
          : 0
        resetCombatGoldAccrual()
        if (preservedFarmGold > 0) {
          setCombatGoldAccrualBuffer(preservedFarmGold)
        }
        _gameLoaded = true
        this.gameLoaded = true
        resetOfflineDepartBeaconState()
        notifySaveSuccess()
        if (preservedFarmGold > 0) {
          void import('~/composables/useCombatGoldCredit').then(({ flushFarmSync }) => {
            void flushFarmSync({ force: true, maxPasses: 8 })
          })
        }

        // Charmes chroma : réconciliation après inventaire + _gameLoaded (évite save bloquée au watch game.vue)
        const pokedexRepair = reconcilePokedexCompletion(player, inventory)
        if (pokedexRepair.changed && !afterAscensionHardReload) {
          await this.saveGameState(false, true)
        }

        const capstoneRepairNeeded =
          capstoneVictoryBeforeLoad &&
          Math.max(
            0,
            Math.floor(
              Number((data.player as { stats?: Record<string, number> }).stats?._btCapstoneDefeated ?? 0),
            ),
          ) < 1
        if (
          !afterAscensionHardReload &&
          (repairedBossKeys > 0 || towerChampionKeyRepaired || capstoneRepairNeeded)
        ) {
          await this.saveGameState(false, true)
        }

        // Pension : état serveur = vérité ; backup pagehide seulement si serveur vide.
        setLastUserIdForLocalBackup(data.player.id)
        const serverDaycare = (data.player as { daycare?: unknown }).daycare
        hydrateDaycareFromServerPayload(serverDaycare)
        tryRestoreDaycareFromPagehideBackup(data.player.id, serverDaycare)

        const gmaxRaidStore = useGmaxRaidStore()
        gmaxRaidStore.hydrate((data.player as any).gmaxRaids ?? (data.player as any).stats?._gmaxRaidsV1)

        // Restore saved teams (convert slug-based format back to local IDs)
        const savedTeamsData = (data.player as any).savedTeams
        if (Array.isArray(savedTeamsData)) {
          const converted = savedTeamsData
            .map((t: any) => ({
              id: t.id,
              name: t.name,
              tags: Array.isArray(t.tags) ? t.tags : [],
              note: typeof t.note === 'string' ? t.note : '',
              pinned: t.pinned === true,
              createdAt: t.createdAt,
              updatedAt: t.updatedAt,
              lastUsedMode: t.lastUsedMode ?? null,
              pokemonIds: (t.pokemons || [])
                .map((p: any) => {
                  const found = inventory.collection.find(
                    (pk) => pk.slug === p.slug && pk.isShiny === (p.isShiny ?? false)
                  )
                  return found?.id ?? -1
                })
                .filter((id: number) => id !== -1),
            }))
          inventory.setSavedTeamsFromServer(converted)
        }

        game.setOfflineReportFromServer({
          report: data.offlineReport ?? null,
          history: data.offlineReportHistory ?? [],
          status: offlineReportStatus,
        })
        if (offlineReportStatus === 'claimable') {
          _offlineReportAcknowledged = false
        }
        if (offlineReportStatus === 'loading' && !_offlineReportHydrationAttempted && !afterAscensionHardReload) {
          _offlineReportHydrationAttempted = true
          void this.hydrateOfflineReportInBackground()
        }

        // Restaure les expéditions actives
        const expeditionStore = useExpeditionStore()
        expeditionStore.setFromLoad((data.player as any).expeditions ?? [])

        // Restaure l'expédition communautaire (chargée en arrière-plan — hors chemin /load).
        if (isNgPlusFreshRun) {
          useCommunityExpeditionStore().clearFromRunReset()
        } else {
          void this.hydrateCommunityExpeditionInBackground()
        }

        repairCombatLoop()
        return null
      } catch (e: any) {
        console.error('Failed to load game state:', e)
        _gameLoaded = false
        this.gameLoaded = false
        this.gameLoadError = toPublicErrorMessage(e, 'Impossible de synchroniser le compte.')
        return null
      } finally {
        this.gameLoading = false
      }
      } finally {
        _loadGameStateDepth -= 1
      }
    },

    /**
     * Marque le rapport offline comme acquitté côté client et annule les hydrations / retries en cours.
     * Évite qu’un GET /offline-report tardif ne déclenche loadGameState() après le claim (rollback).
     */
    acknowledgeOfflineReportSession() {
      _offlineReportAcknowledged = true
      _offlineReportHydrationAttempted = true
      if (_offlineReportRetryTimer) {
        clearTimeout(_offlineReportRetryTimer)
        _offlineReportRetryTimer = null
      }
    },

    async hydrateOfflineReportInBackground() {
      if (!this.isAuthenticated || !_gameLoaded || _offlineReportHydrationInFlight) return
      if (_offlineReportAcknowledged) return
      if (useGameStore().offlineStatus === 'cooldown') return
      _offlineReportHydrationInFlight = true
      try {
        const api = useApi()
        // No short timeout here: offline compute can be heavy for endgame accounts.
        const res = await api.get<OfflineReportSyncResponse>('/api/game/offline-report')
        const player = usePlayerStore()
        if (Array.isArray(res.dailyQuests)) {
          player.dailyQuests = res.dailyQuests
        }
        useGameStore().setOfflineReportFromServer({
          report: res.report ?? null,
          history: res.history ?? [],
          status: res.status ?? 'cooldown',
        })
        // Recharger l’état complet si le serveur a appliqué un rapport (or / pension / œufs / shinies).
        // Ne pas bloquer sur frontierReturnGuard : sinon inventaire obsolète → save-pokemons peut effacer les œufs offline.
        const hasAppliedReport = Boolean(res.report)
        const reportHasHatches =
          (res.report?.hatchEvents?.length ?? 0) > 0 || (res.report?.eggsHatched ?? 0) > 0
        if (
          !_offlineReportAcknowledged &&
          useGameStore().offlineStatus !== 'cooldown' &&
          hasAppliedReport &&
          (res.reload || res.changed || res.pokemonsChanged || reportHasHatches)
        ) {
          await this.loadGameState()
        }
      } catch (e: any) {
        console.warn(
          `[OFFLINE-REPORT] Background hydration failed (status=${e?.status ?? '?'}):`,
          e?.message ?? e
        )
        // Allow one automatic retry a few seconds later without blocking login.
        _offlineReportHydrationAttempted = false
        if (!_offlineReportRetryTimer && this.isAuthenticated && _gameLoaded) {
          _offlineReportRetryTimer = setTimeout(() => {
            _offlineReportRetryTimer = null
            if (!_offlineReportHydrationAttempted) {
              _offlineReportHydrationAttempted = true
              void this.hydrateOfflineReportInBackground()
            }
          }, 5000)
        }
      } finally {
        _offlineReportHydrationInFlight = false
      }
    },

    /** Charge l'expédition communautaire hors du GET /load (4–6 requêtes DB évitées au login). */
    async hydrateCommunityExpeditionInBackground() {
      if (!this.isAuthenticated || !_gameLoaded) return
      try {
        const api = useApi()
        const state = await api.get<CommunityExpeditionState>('/api/game/community-expedition/state')
        useCommunityExpeditionStore().setFromLoad(state)
      } catch (e: unknown) {
        console.warn('[community-expedition] background hydrate failed:', e)
      }
    },

    async markOfflineReportSeen() {
      if (!this.isAuthenticated) return
      try {
        const api = useApi()
        await api.post('/api/game/offline-report/seen')
      } catch {
        // Non bloquant: l'UI bascule déjà en cooldown localement.
      }
    },

    /**
     * Autosave léger via POST /farm-sync (checkpoint + or optionnel).
     */
    async saveGameCheckpoint(keepalive = false): Promise<void> {
      if (!this.isAuthenticated || !_gameLoaded) return
      if (_ascensionFlowActive) return
      if (!keepalive && !isGameTabLeader()) return
      if (useInvocationLockStore().active) return
      if (_saveLock) return

      const { flushFarmSync } = await import('~/composables/useCombatGoldCredit')
      await flushFarmSync({ keepalive })
    },

    /** @param force — bypass 5s throttle (use after server-authoritative gold changes e.g. gacha, so the next save cannot race with stale gold) */
    async saveGameState(
      keepalive = false,
      force = false,
      options?: SaveGameStateOptions,
    ): Promise<GameSavePlayerResult | void> {
      if (!this.isAuthenticated) return
      if (_ascensionFlowActive && !force) return
      if (!keepalive && !force && !isGameTabLeader()) return

      const invocationLock = useInvocationLockStore()
      if (!keepalive && !force && invocationLock.active) {
        // During gacha pull we avoid heavy save/save-pokemons calls that contend on user row lock.
        // Schedule a near-future retry so data still persists right after invocation ends.
        if (!_invocationDeferredSaveTimer) {
          _invocationDeferredSaveTimer = setTimeout(() => {
            _invocationDeferredSaveTimer = null
            if (!this.isAuthenticated) return
            if (useInvocationLockStore().active) return
            void this.saveGameState(false, false).catch((e: unknown) => {
              console.warn('[SAVE] Deferred invocation save failed:', e)
            })
          }, 1200)
        }
        return
      }

      // Block saves until loadGameState has completed successfully
      // This prevents sending empty/default data that would wipe server state
      if (!_gameLoaded) {
        console.warn('[SAVE] Blocked — game state not loaded yet')
        return
      }

      await this.resyncSessionTokenIfMissing()
      if (!this.sessionToken) {
        console.warn('[SAVE] Blocked — missing session token (cookie session without stored token)')
        return
      }

      // Toujours recalculer le snapshot offline avant envoi (sinon taux d’or/DPS restent à 0 → pas de gains côté serveur).
      useGameStore().saveOfflineSnapshot()

      // Sauvegardes forcées : attendre la fin d’une sauvegarde en cours (évite un return vide au shop / gacha).
      if (force) {
        await waitForSaveLock()
      }

      // Throttle: if last save was less than 5s ago, defer (unless keepalive/page unload or force)
      if (!keepalive && !force) {
        const elapsed = Date.now() - _lastSaveTime
        if (elapsed < SAVE_THROTTLE_MS) {
          if (!_throttleTimer) {
            _throttleTimer = setTimeout(() => {
              _throttleTimer = null
              void this.saveGameState().catch((e: unknown) => {
                console.warn('[SAVE] Deferred throttled save failed:', e)
              })
            }, SAVE_THROTTLE_MS - elapsed)
          }
          return
        }
      }

      // Prevent concurrent saves (race condition causes pokemon duplication)
      if (_saveLock) {
        if (force) {
          const unlocked = await waitForSaveLock(3000)
          if (_saveLock || !unlocked) {
            _savePending = true
            return
          }
        } else {
          _savePending = true
          return
        }
      }
      _saveLock = true
      _savePending = false
      _lastSaveTime = Date.now()
      if (_throttleTimer) { clearTimeout(_throttleTimer); _throttleTimer = null }

      let playerSaveResult: GameSavePlayerResult | undefined
      let saveWasRateLimited = false
      let playerSaveOk = false
      let pokemonSaveOk = false

      trackSaveStart()
      try {
        const api = useApi()
        const player = usePlayerStore()
        const inventory = useInventoryStore()
        const game = useGameStore()
        const gmaxRaidStore = useGmaxRaidStore()
        if (this.user?.id) {
          setLastUserIdForLocalBackup(this.user.id)
          persistDaycareLocalBackup()
        }

        const fetchOpts = keepalive ? { keepalive: true } : undefined
        const pokemonsFirst = options?.pokemonsFirst === true
        const forcePokemons = options?.forcePokemons === true

        const includeSavedTeams = force || isSavedTeamsDirty()
        const descenteRunJson = descenteRunSnapshotJson(player.descenteSanctuaireRun)
        const includeDescenteRun =
          force || descenteRunJson !== _lastPersistedDescenteRunJson

        const playerPayload = {
          // Banque confirmée seulement — le buffer farm passe par /farm-sync.
          gold: player.serverConfirmedGold,
          xp: player.xp,
          level: player.level,
          currentGeneration: player.currentGeneration,
          currentZone: player.currentZone,
          currentStage: player.currentStage,
          stageKills: player.stageKills ?? 0,
          teamDpsBonus: player.teamDpsBonus,
          badges: player.badges,
          candies: toSavePlainValue(player.candies),
          defeatedBosses: toSavePlainValue(player.defeatedBosses),
          totalKills: player.totalKills,
          gmaxRaids: toSavePlainValue(gmaxRaidStore.serialize()),
          ...(includeSavedTeams
            ? {
                savedTeams: inventory.savedTeams.map((t) => ({
                  id: t.id,
                  name: t.name,
                  tags: t.tags ?? [],
                  note: t.note ?? '',
                  pinned: t.pinned === true,
                  createdAt: t.createdAt ?? Date.now(),
                  updatedAt: Date.now(),
                  lastUsedMode: t.lastUsedMode ?? null,
                  pokemons: t.pokemonIds
                    .map((id) => {
                      const p = inventory.collection.find((pk) => pk.id === id)
                      return p ? { slug: p.slug, isShiny: p.isShiny } : null
                    })
                    .filter(Boolean),
                })),
              }
            : {}),
          adminVersion: player.adminVersion,
          shinyCharms: player.shinyCharms,
          completedPokedexGens: player.completedPokedexGens,
          combatGeneration: player.isFrontierReturnGuarded() ? null : player.combatGeneration,
          combatZone: player.isFrontierReturnGuarded() ? null : player.combatZone,
          stats: toSavePlainValue(player.stats),
          showcase: toSavePlainValue(player.showcase),
          achievements: toSavePlainValue(player.achievements),
          selectedTitle: player.selectedTitle,
          selectedAvatarFrame: player.selectedAvatarFrame,
          selectedProfileBanner: player.selectedProfileBanner,
          selectedHudTheme: player.selectedHudTheme,
          hideTeamFromProfile: player.hideTeamFromProfile,
          hideSupporterBadge: player.hideSupporterBadge,
          audioPreferences: toSavePlainValue(player.audioPreferences),
          inclusiveWriting: player.inclusiveWriting,
          combatAutomation: toSavePlainValue(player.combatAutomation),
          lastSelectedFarmRoute: toSavePlainValue(player.lastSelectedFarmRoute),
          favoriteFarmRoute: toSavePlainValue(player.favoriteFarmRoute),
          offlineShinyReveal: player.offlineShinyReveal,
          goldNonce: player.goldNonce,
          battleTowerDefeatedTypes: toSavePlainValue(player.battleTowerDefeatedTypes),
          battleTowerZone: player.battleTowerZone,
          descenteSanctuaireShards: player.descenteSanctuaireShards,
          ...(includeDescenteRun
            ? { descenteSanctuaireRun: toSavePlainValue(player.descenteSanctuaireRun) }
            : {}),
          snapshotDps: game.snapshotDps,
          snapshotZoneDps: game.snapshotZoneDps,
          snapshotGoldRate: game.snapshotGoldRate,
          snapshotEnemyMaxHp: game.snapshotEnemyMaxHp,
          snapshotGoldReward: game.snapshotGoldReward,
          sessionToken: this.sessionToken,
          serverBootId: this.serverBootId,
        } as Record<string, unknown>

        let playerBootResyncRetried = false
        const runPlayerSave = async (): Promise<boolean | 'reload'> => {
          playerSaveOk = false
          try {
            if (keepalive) {
              void api.post('/api/game/save', playerPayload, fetchOpts).catch((e: unknown) => {
                console.warn('[SAVE] Keepalive player save failed:', e)
              })
              playerSaveOk = true
              return true
            }
            const saveResult = await api.post<GameSavePlayerResult>('/api/game/save', playerPayload)
            playerSaveResult = saveResult
            playerSaveOk = true
            if (saveResult?.reload) {
              if (saveResult.code === 'SERVER_UPDATED') {
                // Écriture refusée avant persistance : rejouer avec le nouveau boot id
                // plutôt que recharger, ce qui perdrait le contenu du payload.
                if (resyncServerBootId(this, saveResult, playerBootResyncRetried)) {
                  playerBootResyncRetried = true
                  playerPayload.serverBootId = this.serverBootId
                  _serverUpdateReloadPending = true
                  return runPlayerSave()
                }
                // Flotte encore mixte : on garde l'état en mémoire, le prochain
                // cycle de sauvegarde le persistera. Aucun reload, aucune perte.
                _serverUpdateReloadPending = false
                return 'reload'
              }
              console.log('[SAVE] Admin override detected — reloading game state')
              useCombatStore().reset()
              usePlayerStore().stageKills = 0
              await this.loadGameState()
              repairCombatLoop()
              return 'reload'
            }
            if (saveResult) {
              player.applySaveGoldResponse(saveResult)
              void import('~/composables/useCombatGoldCredit').then(
                ({ combatGoldAccrual, setCombatGoldAccrualBuffer }) => {
                  setCombatGoldAccrualBuffer(combatGoldAccrual.value)
                },
              )
              if (includeSavedTeams) {
                resetSavedTeamsDirty()
              }
              if (includeDescenteRun) {
                _lastPersistedDescenteRunJson = descenteRunJson
              }
              if (
                saveResult.totalKillsAdjusted === true &&
                typeof saveResult.totalKills === 'number'
              ) {
                player.totalKills = Math.max(0, Math.floor(saveResult.totalKills))
              }
              if (Array.isArray(saveResult.dailyQuests)) {
                player.dailyQuests = saveResult.dailyQuests
              }
              if (Array.isArray(saveResult.runContracts)) {
                player.runContracts = saveResult.runContracts
              }
              if (Array.isArray((saveResult as any).weeklyQuests)) {
                player.applyWeeklyQuestsFromServer((saveResult as any).weeklyQuests)
              }
              if (Array.isArray((saveResult as any).monthlyQuests)) {
                player.applyMonthlyQuestsFromServer((saveResult as any).monthlyQuests)
              }
            }
            return true
          } catch (e: any) {
            if (e?.status === 409) {
              handleSessionConflict409(this)
              return 'reload'
            }
            if (e?.status === 429 || e?.status === 503) {
              saveWasRateLimited = true
              console.warn('[SAVE] Rate limited or lane busy — prochaine sync un peu plus tard.')
              scheduleSaveAfterRateLimit(e?.retryAfter)
            } else {
              console.error(`[SAVE] Player save failed (status=${e?.status ?? '?'}):`, e?.message ?? e)
            }
            return false
          }
        }

        // Un unload sans mutation Pokémon ne doit jamais réenvoyer un snapshot complet :
        // après une lecture périmée, cela pouvait supprimer des lignes présentes uniquement
        // côté serveur. Les changements réels restent couverts par le dirty tracking.
        const shouldSavePokemons = forcePokemons || isPokemonsDirty()
        const dirtyGen = snapshotPokemonsDirtyGen()
        pokemonSaveOk = false

        let pokemonBootResyncRetried = false
        const runPokemonSave = async (): Promise<boolean | 'reload'> => {
          if (!shouldSavePokemons) {
            pokemonSaveOk = true
            return true
          }
          try {
            // Delta save: only send pokemon that changed since the last save.
            // Falls back to full collection when explicitly forced or when no dirty IDs
            // are tracked (e.g. first save, bulk repair before an item evolution).
            // Toujours inclure l’équipe active avec tout delta : sinon un seul slot marqué « dirty »
            // peut omettre d’autres lignes d’équipe et désynchroniser les team_slot au refresh (F5).
            const dirtyIds = new Set<number>(getDirtyPokemonIds())
            if (dirtyIds.size > 0) {
              for (const p of inventory.team) {
                dirtyIds.add(p.id)
              }
            }
            const sourceCollection =
              !forcePokemons &&
              dirtyIds.size > 0 &&
              dirtyIds.size < inventory.collection.length
                ? inventory.collection.filter((p) => dirtyIds.has(p.id))
                : inventory.collection

            const snapshotSourceServerIds = sourceCollection.map((p) => p.serverId ?? null)
            // Les sauvegardes Pokémon sont uniquement additives/mises à jour. L'absence
            // d'une ligne dans un snapshot client n'est jamais une demande de suppression.
            const isDelta = true
            const pokemons = sourceCollection.map((p) => {
              return {
                serverId: p.serverId ?? null,
                slug: p.slug,
                pokedexId: getIdForSlug(p.slug),
                gen: getGenForSlug(p.slug),
                level: p.level,
                xp: p.xp,
                stars: p.stars,
                isShiny: p.isShiny,
                rarity: p.rarity ?? 'common',
                teamSlot: p.teamSlot,
                isFavorite: p.isFavorite === true,
                cosmeticFormSlug: p.cosmeticFormSlug ?? null,
              }
            })

            const buildPokemonsPayload = (delta: boolean) => ({
              pokemons,
              delta,
              adminVersion: player.adminVersion,
              sessionToken: this.sessionToken,
              serverBootId: this.serverBootId,
            })

            if (pokemons.length > 0) {
              let pokemonsPayload = buildPokemonsPayload(isDelta)
              let payloadSize = JSON.stringify(pokemonsPayload).length
              const KEEPALIVE_LIMIT = 60_000
              if (
                keepalive &&
                payloadSize > KEEPALIVE_LIMIT &&
                dirtyIds.size > 0 &&
                dirtyIds.size < inventory.collection.length
              ) {
                pokemonsPayload = buildPokemonsPayload(true)
                payloadSize = JSON.stringify(pokemonsPayload).length
              }
              if (keepalive && payloadSize > KEEPALIVE_LIMIT) {
                console.warn(`[SAVE] Pokemon payload too large for keepalive (${payloadSize}B), skipping`)
                pokemonSaveOk = false
              } else if (keepalive) {
                void api.post('/api/game/save-pokemons', pokemonsPayload, fetchOpts).catch((e: unknown) => {
                  console.warn('[SAVE] Keepalive Pokémon save failed:', e)
                })
                pokemonSaveOk = true
              } else {
                const pokRes = await api.post<GameSavePlayerResult & { ids?: (number | null)[] }>(
                  '/api/game/save-pokemons',
                  pokemonsPayload
                )
                if (pokRes?.reload) {
                  if (pokRes.code === 'SERVER_UPDATED') {
                    if (resyncServerBootId(this, pokRes, pokemonBootResyncRetried)) {
                      pokemonBootResyncRetried = true
                      // buildPokemonsPayload relit this.serverBootId : le rejeu
                      // reconstruit le payload avec le nouvel id.
                      _serverUpdateReloadPending = true
                      return runPokemonSave()
                    }
                    _serverUpdateReloadPending = false
                    return 'reload'
                  }
                  await this.loadGameState()
                  repairCombatLoop()
                  return 'reload'
                }
                pokemonSaveOk = true
                if (pokRes?.ids && Array.isArray(pokRes.ids)) {
                  const newIds = pokRes.ids
                  for (let i = 0; i < sourceCollection.length; i++) {
                    const poke = sourceCollection[i]
                    if (!poke) continue
                    if ((poke.serverId ?? null) !== snapshotSourceServerIds[i]) continue
                    const sid = newIds[i]
                    if (sid != null) poke.serverId = sid
                  }
                }
              }
            } else {
              pokemonSaveOk = true
            }
            if (pokemonSaveOk) {
              resetDaycareLevelGateBlock()
              resetPokemonsDirty(dirtyGen)
              const current = getDirtyPokemonIds() as Set<number>
              dirtyIds.forEach((id) => current.delete(id))
              if (current.size === 0) {
                clearDirtyPokemonIds()
              }
            }
            return pokemonSaveOk ? true : false
          } catch (e: any) {
            if (e?.status === 409) {
              handleSessionConflict409(this)
              return 'reload'
            }
            if (e?.status === 429 || e?.status === 503) {
              saveWasRateLimited = true
              console.warn('[SAVE] Pokémon save rate limited or lane busy.')
              scheduleSaveAfterRateLimit(e?.retryAfter)
            } else {
              console.error(`[SAVE] Pokémon save failed (status=${e?.status ?? '?'}):`, e?.message ?? e)
            }
            return false
          }
        }

        const saveSteps = pokemonsFirst
          ? [runPokemonSave, runPlayerSave]
          : [runPlayerSave, runPokemonSave]
        for (const step of saveSteps) {
          const stepResult = await step()
          if (stepResult === 'reload') return playerSaveResult
        }

        const pokemonSaveRequired = shouldSavePokemons
        const saveFullySucceeded =
          playerSaveOk && (!pokemonSaveRequired || pokemonSaveOk)
        if (saveFullySucceeded) {
          notifySaveSuccess()
          // Le rejeu après SERVER_UPDATED a abouti : l'état est persisté côté serveur,
          // recharger pour récupérer le nouveau bundle ne perd désormais plus rien.
          if (_serverUpdateReloadPending) {
            _serverUpdateReloadPending = false
            triggerServerUpdatedReload(true)
          }
        } else if (!saveWasRateLimited && !keepalive) {
          notifySaveFailure()
        }
      } finally {
        trackSaveEnd(
          !playerSaveOk && !pokemonSaveOk && !saveWasRateLimited
            ? new Error('save_failed')
            : undefined,
        )
        // Always release lock, even on error or early return
        _saveLock = false
      }

      if (!keepalive) {
        void import('~/composables/useCombatGoldCredit').then(
          ({ flushCombatGoldCredit, hasPendingCombatGold }) => {
            if (!hasPendingCombatGold()) return
            return flushCombatGoldCredit().catch((e: unknown) => {
              console.warn('[SAVE] Post-save credit-gold failed:', e)
            })
          },
        )
      }

      // Retry if a save was requested while we were saving
      if (_savePending) {
        _savePending = false
        void this.saveGameState().catch((e: unknown) => {
          console.warn('[SAVE] Pending retry save failed:', e)
        })
      }

      return playerSaveResult
    },

    /**
     * Pousse uniquement l’or (bac client → serveur) via POST /api/game/sync-gold.
     * Ne compte pas dans le rate limit des sauvegardes complètes ; évite 429 quand la farm pousse souvent le pending.
     * Skip si un save complet est en cours (_saveLock) pour ne pas doubler `applySaveGoldResponse`.
     */
    async syncGoldToServer(options?: {
      allowDuringSaveLock?: boolean
      applyResponse?: boolean
      bypassInvocationLock?: boolean
    }): Promise<GameSavePlayerResult | void> {
      if (!this.isAuthenticated || !_gameLoaded) return
      if (_ascensionFlowActive) return
      if (!isGameTabLeader()) return
      const allowDuringSaveLock = options?.allowDuringSaveLock === true
      const applyResponse = options?.applyResponse !== false
      const bypassInvocationLock = options?.bypassInvocationLock === true
      if (!bypassInvocationLock && useInvocationLockStore().active) return
      if (_saveLock && !allowDuringSaveLock) return

      await this.resyncSessionTokenIfMissing()
      if (!this.sessionToken) return

      const api = useApi()
      const player = usePlayerStore()
      try {
        const postSyncGold = () =>
          api.post<GameSavePlayerResult>('/api/game/sync-gold', {
            // Banque confirmée seulement — le buffer farm passe par /farm-sync.
            gold: player.serverConfirmedGold,
            goldNonce: player.goldNonce,
            adminVersion: player.adminVersion,
            sessionToken: this.sessionToken,
            serverBootId: this.serverBootId,
          })
        let saveResult = await postSyncGold()
        // Refus pour version serveur : adopter le boot id et rejouer une fois,
        // l'or n'ayant pas été écrit par la requête refusée.
        if (saveResult?.reload && saveResult.code === 'SERVER_UPDATED') {
          if (resyncServerBootId(this, saveResult, false)) {
            saveResult = await postSyncGold()
          }
        }
        if (saveResult?.reload) {
          if (saveResult.code === 'SERVER_UPDATED') {
            // Flotte encore mixte : ne pas recharger, la sauvegarde suivante réessaiera.
            return saveResult
          }
          console.log('[SYNC-GOLD] Admin override detected — reloading game state')
          useCombatStore().reset()
          usePlayerStore().stageKills = 0
          await this.loadGameState()
          repairCombatLoop()
          return saveResult
        }
        if (saveResult && applyResponse) {
          player.applySaveGoldResponse(saveResult)
          void import('~/composables/useCombatGoldCredit').then(
            ({ combatGoldAccrual, setCombatGoldAccrualBuffer }) => {
              // Réaffiche le pending farm après mise à jour de la banque.
              setCombatGoldAccrualBuffer(combatGoldAccrual.value)
            },
          )
        }
        return saveResult
      } catch (e: any) {
        if (e?.status === 409) {
          handleSessionConflict409(this)
          return
        }
        if (e?.status === 429) {
          console.warn('[SYNC-GOLD] Rate limited — prochaine sync un peu plus tard.')
        } else {
          console.error(`[SYNC-GOLD] Failed (status=${e?.status ?? '?'}):`, e?.message ?? e)
        }
        return undefined
      }
    },

    /**
     * Heartbeat offline léger pour fiabiliser `_offlineLastSeenAtSec` + snapshot
     * sans envoyer tout le payload de sauvegarde.
     */
    async sendOfflineHeartbeat(
      keepalive = false,
      reason: 'hidden' | 'pagehide' | 'beforeunload' | 'manual' = 'manual'
    ): Promise<GameSavePlayerResult | void> {
      if (!this.isAuthenticated || !_gameLoaded) return
      if (_ascensionFlowActive) return

      await this.resyncSessionTokenIfMissing()
      if (!this.sessionToken) return

      const player = usePlayerStore()
      const game = useGameStore()
      game.saveOfflineSnapshot()

      const api = useApi()
      const payload = {
        snapshotDps: game.snapshotDps,
        snapshotZoneDps: game.snapshotZoneDps,
        snapshotGoldRate: game.snapshotGoldRate,
        snapshotEnemyMaxHp: game.snapshotEnemyMaxHp,
        snapshotGoldReward: game.snapshotGoldReward,
        adminVersion: player.adminVersion,
        sessionToken: this.sessionToken,
        serverBootId: this.serverBootId,
        awayReason: reason,
      } as Record<string, unknown>

      try {
        if (keepalive) {
          void api.post('/api/game/offline-heartbeat', payload, { keepalive: true }).catch((e: unknown) => {
            console.warn('[OFFLINE-HEARTBEAT] Keepalive failed:', e)
          })
          return
        }

        let res = await api.post<GameSavePlayerResult>('/api/game/offline-heartbeat', payload)
        // Refus pour version serveur : le snapshot n'a pas été bufferisé côté serveur.
        // On adopte le boot id et on rejoue une fois avant d'abandonner.
        if (res?.reload && res.code === 'SERVER_UPDATED') {
          if (resyncServerBootId(this, res, false)) {
            payload.serverBootId = this.serverBootId
            res = await api.post<GameSavePlayerResult>('/api/game/offline-heartbeat', payload)
          }
        }
        if (res?.reload) {
          if (res.code === 'SERVER_UPDATED') {
            // Flotte encore mixte : ne pas recharger, le heartbeat suivant réessaiera.
            return res
          }
          console.log('[OFFLINE-HEARTBEAT] Admin override detected — reloading game state')
          useCombatStore().reset()
          usePlayerStore().stageKills = 0
          await this.loadGameState()
          repairCombatLoop()
          return res
        }
        return res
      } catch (e: any) {
        void reportClientError({
          kind: 'heartbeat_trace',
          message: '[offline-heartbeat] sent_failed',
          meta: {
            reason,
            keepalive,
            status: e?.status ?? null,
            error: e?.message ?? String(e),
          },
        })
        if (e?.status === 409) {
          handleSessionConflict409(this)
          return
        }
        if (e?.status === 429) {
          console.warn('[OFFLINE-HEARTBEAT] Rate limited.')
        } else {
          console.warn(
            `[OFFLINE-HEARTBEAT] Failed (status=${e?.status ?? '?'}):`,
            e?.message ?? e
          )
        }
      }
    },

    /**
     * Flush local gold/state to the server before a server-authoritative spend (gacha, etc.).
     * Without this, HUD gold can be ahead of the DB if the 5s save throttle hasn’t run yet.
     */
    /**
     * Après une invocation : regroupe stats/quêtes en une seule sauvegarde (~2,5 s après le dernier tirage).
     * Les Pokémon sont déjà persistés côté serveur par POST /api/invocations.
     */
    schedulePostGachaSave() {
      if (!this.isAuthenticated || !_gameLoaded) return
      if (_postGachaSaveTimer) clearTimeout(_postGachaSaveTimer)
      _postGachaSaveTimer = setTimeout(() => {
        _postGachaSaveTimer = null
        void this.saveGameState(false, true).catch((e: unknown) => {
          console.warn('[SAVE] Post-gacha save failed:', e)
        })
      }, POST_GACHA_SAVE_DEBOUNCE_MS)
    },

    /** Sauvegarde immédiate si une sync post-gacha est en attente (OK / quitter la page). */
    async flushPostGachaSaveIfPending(): Promise<void> {
      if (!_postGachaSaveTimer) return
      clearTimeout(_postGachaSaveTimer)
      _postGachaSaveTimer = null
      await this.saveGameState(false, true)
    },

    /**
     * Tente de créditer l’or farm en attente avant une dépense serveur.
     * Retourne false si un buffer farm reste non crédité (la dépense ne doit pas
     * s’appuyer sur un HUD gonflé par de l’or encore local).
     */
    async flushGameStateForSpend(): Promise<void> {
      if (!this.isAuthenticated || !_gameLoaded) return
      const { flushCombatGoldCredit, hasPendingCombatGold } = await import(
        '~/composables/useCombatGoldCredit'
      )
      if (!hasPendingCombatGold()) return
      for (let attempt = 0; attempt < 3; attempt++) {
        const flushed = await flushCombatGoldCredit({ force: true, maxPasses: 8 })
        if (!hasPendingCombatGold()) return
        if (flushed && !hasPendingCombatGold()) return
        await new Promise((r) => setTimeout(r, 250 * (attempt + 1)))
      }
      if (hasPendingCombatGold()) {
        console.warn(
          '[SPEND-FLUSH] Farm gold sync incomplete — spend proceeds on confirmed bank balance.',
        )
      }
    },

    async flushGoldForSpendWithRetry(): Promise<boolean> {
      if (!this.isAuthenticated || !_gameLoaded) return false
      await this.flushGameStateForSpend()
      const { hasPendingCombatGold } = await import('~/composables/useCombatGoldCredit')
      // true = flush OK (plus de pending) ; false = encore du buffer local
      return !hasPendingCombatGold()
    },

    /** Persiste les consommations de bonbons (et le reste du save) avant un achat boutique. */
    async flushCandiesForSpendWithRetry(): Promise<boolean> {
      for (let attempt = 0; attempt < 2; attempt++) {
        try {
          await this.saveGameState(false, true)
          return true
        } catch {
          /* second essai */
        }
      }
      return false
    },
  },
})
